import { faLayerGroup, faOilWell, faTag } from "@elynx/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronRight, CurtainsClosedRounded, ExpandMore, SettingsRemoteRounded, WebStoriesRounded } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import Enumerable from "linq";
import { JSX, useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useLocation } from "react-router";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import useCustomizedNavigationRoutes from "../../hooks/use-customized-navigation-routes/UseCustomizedNavigationRoutes";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { IRoute, UIViewSubCategory } from "../../routes/routes";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import { IUICustomizationViewCategorySettingsDTO } from "../../services/ui-customization-data-service/Models";
import NavigationViewListUtils from "./NavigationViewListUtils";

export type NavigationViewListSectionV2Props = {
    open: boolean;
    navigationItem: INavigationItem;
    customizationSettings: IUICustomizationViewCategorySettingsDTO | undefined;
    viewAll: boolean;
    railMode?: boolean;
    contextualNavDrawerOpen?: boolean;
    onToggleContextualNavDrawer?: (navigationItem: INavigationItem) => void;
    onClick: (navigationItem: INavigationItem, route: IRoute, shiftKey: boolean, ctrlKey: boolean) => void;
    onOpen?: (open: boolean) => void;
};

export default function NavigationViewGroupedListSectionV2({
    open,
    navigationItem,
    customizationSettings,
    viewAll,
    railMode,
    contextualNavDrawerOpen,
    onToggleContextualNavDrawer,
    onClick,
    onOpen
}: NavigationViewListSectionV2Props): JSX.Element {
    const location = useLocation();
    const routeContext = useRouteContext();
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const isPhone = isMobile && !isTablet;

    const navigationRoutes = useCustomizedNavigationRoutes(
        navigationItem.objectTypeID,
        navigationItem.objectID,
        customizationSettings,
        viewAll
    );

    const isGrouped = !(customizationSettings?.ungrouped ?? false);

    const activeRoute = navigationRoutes.find(o => o.fullPath === location.pathname);

    const groupedNavigationRoutes = Enumerable.from(navigationRoutes)
        .where(o => o.parentViewID === undefined)
        .groupBy(
            o => isGrouped
                ? o.subCategory ?? UIViewSubCategory.Uncategorized
                : UIViewSubCategory.Uncategorized,
            o => o,
            (key, element) => ({
                subCategory: key,
                routes: element
                    .orderBy(o => o.order ?? 9999)
                    .toArray()
            }))
        .orderBy(o => o.subCategory ?? UIViewSubCategory.Uncategorized)
        .toArray();

    const isActiveLevel = navigationItem.objectTypeID === routeContext.contextObject?.objectType;

    return (
        <>
            <ListItem disablePadding>
                <Accordion
                    disableGutters={true}
                    elevation={0}
                    expanded={open}
                    onChange={(_, expanded) => {
                        if (open && !isPhone) {
                            onToggleContextualNavDrawer?.(navigationItem);
                        }
                        onOpen?.(expanded)
                    }}
                    slotProps={{
                        root: {
                            sx: {
                                width: 1
                            }
                        }
                    }}
                    sx={{ backgroundColor: "green" }}
                >
                    <AccordionSummary
                        ref={anchorRef}
                        expandIcon={
                            (isActiveLevel && !isPhone)
                                ? <ChevronRight
                                    sx={{
                                        color: "primary.contrastText",
                                        transform: contextualNavDrawerOpen ? "rotate(0deg)" : "rotate(180deg)",
                                        transition: theme => theme.transitions.create("transform", {
                                            duration: theme.transitions.duration.shortest
                                        })
                                    }}
                                />
                                : (!railMode && !isActiveLevel)
                                    ? <ExpandMore
                                        sx={{
                                            color: "text.primary"
                                        }}
                                    />
                                    : undefined
                        }
                        sx={{
                            overflow: 'hidden',
                            backgroundColor: isActiveLevel
                                ? "primary.main"
                                : "transparent",

                            color: isActiveLevel
                                ? "primary.contrastText"
                                : "text.primary"
                        }}
                        slotProps={{
                            content: {
                                sx: {
                                    overflow: 'hidden',
                                    margin: 0,
                                    height: theme => theme.spacing(8)
                                }
                            },
                            expandIconWrapper: {
                                sx: {
                                    marginRight: -1
                                }
                            }
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: "center",
                                width: 1
                            }}>
                            <Tooltip
                                arrow
                                placement="right"
                                title={railMode
                                    ? `${getObjectTypeName(navigationItem.objectTypeID)} - ${navigationItem.title}`
                                    : undefined
                                }
                            >
                                <Box>
                                    {navigationItem.objectTypeID === eLynxObjectTypeEnum.Group
                                        ? <FontAwesomeIcon icon={faLayerGroup} size="xl" />
                                        : navigationItem.objectTypeID === eLynxObjectTypeEnum.Device
                                            ? <SettingsRemoteRounded />
                                            : navigationItem.objectTypeID === eLynxObjectTypeEnum.TankBattery
                                                ? <WebStoriesRounded />
                                                : navigationItem.objectTypeID === eLynxObjectTypeEnum.Tank
                                                    ? <CurtainsClosedRounded />
                                                    : navigationItem.objectTypeID === eLynxObjectTypeEnum.Well
                                                        ? <FontAwesomeIcon icon={faOilWell} size="xl" />
                                                        : navigationItem.objectTypeID === eLynxObjectTypeEnum.Tag
                                                            ? <FontAwesomeIcon icon={faTag} size="xl" />
                                                            : undefined
                                    }
                                </Box>
                            </Tooltip>

                            {!railMode &&
                                <Stack
                                    sx={{
                                        width: 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: "small",
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis'
                                        }}>
                                        {getObjectTypeName(navigationItem.objectTypeID)}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: "bold",
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis'
                                        }}>
                                        {navigationItem.title}
                                    </Typography>
                                </Stack>
                            }
                        </Stack>
                    </AccordionSummary>

                    <AccordionDetails
                        sx={{
                            padding: 0
                        }}
                    >
                        <List
                            disablePadding
                            sx={{
                                overflow: "hidden"
                            }}
                        >
                            {groupedNavigationRoutes
                                .filter(o => o.subCategory !== undefined && o.routes.length > 0)
                                .map((routeGroup, index) =>
                                    <ListItem
                                        key={index}
                                        disablePadding
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start"
                                        }}
                                    >
                                        {isGrouped && (
                                            !railMode
                                                ? (
                                                    <ListItemText
                                                        disableTypography
                                                        sx={{
                                                            paddingLeft: 2,
                                                            paddingRight: 2
                                                        }}
                                                        primary={
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    color: "text.secondary",
                                                                    padding: 0,
                                                                    margin: 0
                                                                }}
                                                            >
                                                                {NavigationViewListUtils.getSubcategoryLabel(routeGroup.subCategory!)}
                                                            </Typography>
                                                        }
                                                    />
                                                )
                                                : (
                                                    <Stack
                                                        direction="row"
                                                        sx={{
                                                            alignItems: "center",
                                                            justifyContent: "flex-start",
                                                            width: 1,
                                                            height: 22,
                                                            marginBottom: 0.5,
                                                            marginTop: 0.5,
                                                            paddingLeft: 2,
                                                            paddingRight: 2

                                                        }}
                                                    >
                                                        <Box sx={{
                                                            backgroundColor: "divider",
                                                            height: "2px",
                                                            width: "24px",
                                                        }}>
                                                        </Box>
                                                    </Stack>
                                                )
                                        )}

                                        <List
                                            disablePadding
                                            sx={{
                                                width: 1
                                            }}
                                        >
                                            {routeGroup.routes.map((route, index) =>
                                                <ListItem
                                                    key={index}
                                                    disablePadding
                                                >
                                                    <ListItemButton
                                                        sx={{
                                                            backgroundColor: theme =>
                                                                route.viewID === (activeRoute?.parentViewID ?? activeRoute?.viewID)
                                                                    ? theme.alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity)
                                                                    : "transparent",
                                                        }}
                                                        onClick={
                                                            (event) => onClick(
                                                                navigationItem,
                                                                route,
                                                                event.shiftKey,
                                                                event.ctrlKey
                                                            )
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <Tooltip arrow placement="right" title={railMode
                                                                ? route.name
                                                                : undefined
                                                            }>
                                                                <Box>
                                                                    {<route.icon />}
                                                                </Box>
                                                            </Tooltip>
                                                        </ListItemIcon>
                                                        <ListItemText primary={route.name} />
                                                    </ListItemButton>
                                                </ListItem>
                                            )}
                                        </List>
                                    </ListItem>
                                )
                            }
                        </List>
                    </AccordionDetails>
                </Accordion >
            </ListItem >
            <Divider />
            {/* {(isActiveLevel && activeRoute && menuOpen) &&
                <>
                    {navigationItem.objectTypeID === eLynxObjectTypeEnum.Group
                        ? <BreadCrumbGroupMenu
                            open={menuOpen}
                            anchorEl={anchorRef.current}
                            placement="right"
                            offset={[0, -8]}
                            route={activeRoute!}
                            item={navigationItem}
                            onClick={onClick}
                            onClose={() => { setMenuOpen(false) }}
                            offline={false}
                        />
                        : <BreadCrumbObjectMenu
                            open={menuOpen}
                            anchorEl={anchorRef.current}
                            placement="right"
                            offset={[0, -8]}
                            route={activeRoute!}
                            item={navigationItem}
                            onClick={onClick}
                            onClose={() => { setMenuOpen(false) }}
                            offline={false}
                        />
                    }
                </>
            } */}
        </>
    );
}